import {useCallback} from 'react'
import {useSearchParams} from 'react-router-dom'
import {URL_PARAMS, URL_PARAM_DEFAULTS} from './urlParams'

export interface UseUrlPaginationReturn {
  currentPage: number
  setPage: (page: number) => void
  resetPage: () => void
}

/**
 * Custom hook for managing pagination state synchronized with URL
 * Enables bookmarkable and shareable dashboard views with preserved page numbers
 *
 * @param paramName - URL parameter name (default: URL_PARAMS.PAGE). Use URL_PARAMS.PROGRAM_PAGE for program-level pagination.
 *
 * @example
 * // Simple pagination usage
 * const {currentPage, setPage, resetPage} = useUrlPagination()
 * // URL: /team/123?page=2
 *
 * // Program-level pagination
 * const {currentPage, setPage, resetPage} = useUrlPagination(URL_PARAMS.PROGRAM_PAGE)
 * // URL: /program/456?programPage=3
 *
 * // Read current page
 * console.log(currentPage) // 2
 *
 * // Set page
 * setPage(3)
 *
 * // Reset to page 1
 * resetPage()
 */
export const useUrlPagination = (paramName: string = URL_PARAMS.PAGE): UseUrlPaginationReturn => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Get current page from URL or use default
  const pageParam = searchParams.get(paramName)
  const currentPage = pageParam ? parseInt(pageParam, 10) : URL_PARAM_DEFAULTS.PAGE

  // Validate page number (must be positive integer)
  const validatedPage = !isNaN(currentPage) && currentPage > 0 ? currentPage : URL_PARAM_DEFAULTS.PAGE

  // Set page in URL
  const setPage = useCallback(
    (page: number) => {
      // Validate page number
      if (!page || page < 1 || !Number.isInteger(page)) {
        console.warn(`Invalid page number: ${page}. Must be a positive integer.`)
        return
      }

      setSearchParams(
        params => {
          const newParams = new URLSearchParams(params)

          if (page === URL_PARAM_DEFAULTS.PAGE) {
            // Remove page param if it's the default (page 1)
            newParams.delete(paramName)
          } else {
            newParams.set(paramName, page.toString())
          }

          return newParams
        },
        {replace: true},
      )
    },
    [setSearchParams, paramName],
  )

  // Reset to page 1
  const resetPage = useCallback(() => {
    setSearchParams(
      params => {
        const newParams = new URLSearchParams(params)
        newParams.delete(paramName)
        return newParams
      },
      {replace: true},
    )
  }, [setSearchParams, paramName])

  return {
    currentPage: validatedPage,
    setPage,
    resetPage,
  }
}
